import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TruckIcon, CoinsIcon, WheatIcon, HeartHandshakeIcon, ArrowRightIcon, CheckIcon } from '../components/icons/Icons';
import { useSettings } from '../context/SettingsContext';
import './Portfolio.css';

const Portfolio = () => {
  const { settings } = useSettings();
  const sectors = [
    {
      icon: TruckIcon,
      title: 'Supply & Logistics',
      description: 'Enhancing value chains and ensuring efficient movement of goods across sectors, supporting both local and regional trade.',
      features: [
        'Value chain optimization',
        'Regional trade support',
        'Efficient goods movement',
        'Supply chain management'
      ]
    },
    {
      icon: CoinsIcon,
      title: 'Finance',
      description: 'Providing innovative financial solutions and supporting inclusive access to capital for individuals, SMEs, and strategic projects.',
      features: [
        'Innovative financial solutions',
        'SME capital access',
        'Strategic project funding',
        'Inclusive finance initiatives'
      ]
    },
    {
      icon: WheatIcon,
      title: 'Agriculture',
      description: 'Investing in sustainable farming, agri-processing, and value addition to strengthen food security and rural livelihoods.',
      features: [
        'Sustainable farming practices',
        'Agri-processing investments',
        'Food security initiatives',
        'Rural livelihood support'
      ]
    },
    {
      icon: HeartHandshakeIcon,
      title: 'Investment Partnerships',
      description: 'Maintaining strategic investment partnerships with various companies across diverse industries for collaborative growth.',
      features: [
        'Strategic partnerships',
        'Cross-industry investments',
        'Collaborative growth',
        'Shared value creation'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Portfolio | Fuwali Investments Zambia Limited</title>
        <meta name="description" content="Explore Fuwali Investments' diversified portfolio spanning Supply & Logistics, Finance, Agriculture, and strategic partnerships in Zambia." />
        <link rel="canonical" href="https://fuwalizambia.com/portfolio" />
        <meta property="og:title" content="Our Portfolio | Fuwali Investments Zambia Limited" />
        <meta property="og:description" content="Explore Fuwali Investments' diversified portfolio spanning Supply & Logistics, Finance, Agriculture, and strategic partnerships." />
        <meta property="og:url" content="https://fuwalizambia.com/portfolio" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Page Hero */}
      <section className="page-hero" style={settings.hero_bg_portfolio ? { backgroundImage: `url(${settings.hero_bg_portfolio})` } : {}}>
        <div className="page-hero-overlay"></div>
        <div className="container">
          <span className="page-hero-label">Our Portfolio</span>
          <h1 className="page-hero-title">The Business Core</h1>
          <p className="page-hero-subtitle">
            Our business is built on a foundation of strategic diversification and purposeful impact across key sectors.
          </p>
        </div>
      </section>

      {/* Portfolio Intro */}
      <section className="section portfolio-intro">
        <div className="container">
          <div className="portfolio-intro-content">
            <p className="portfolio-intro-text">
              At Fuwali Investments Zambia Limited, our business is built on a foundation of strategic diversification and purposeful impact. Our core focus spans key sectors that drive national development and economic resilience: <strong>Supply, Logistics, Finance, and Agriculture</strong>.
            </p>
            <p className="portfolio-intro-text">
              Through this dynamic portfolio, Fuwali continues to position itself as a key player in Zambia's economic landscape, driving innovation, empowering communities, and delivering long-term returns with purpose.
            </p>
          </div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="section sectors-section">
        <div className="container">
          <div className="sectors-grid">
            {sectors.map((sector, index) => (
              <div key={index} className="sector-card">
                <div className="sector-header">
                  <div className="sector-icon">
                    <sector.icon size={36} />
                  </div>
                  <h3 className="sector-title">{sector.title}</h3>
                </div>
                <p className="sector-description">{sector.description}</p>
                <ul className="sector-features">
                  {sector.features.map((feature, idx) => (
                    <li key={idx}>
                      <CheckIcon size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section className="section partnerships-section">
        <div className="container">
          <div className="partnerships-content">
            <span className="section-label">Beyond Core Operations</span>
            <h2 className="section-title text-left">Strategic Partnerships</h2>
            <p className="partnerships-text">
              Beyond our core operations, we maintain <strong>investment partnerships</strong> with various companies across diverse industries, reinforcing our commitment to collaborative growth and shared value creation.
            </p>
            <p className="partnerships-text">
              These partnerships allow us to extend our impact beyond our primary sectors, contributing to a more interconnected and resilient Zambian economy.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Explore Partnership Opportunities <ArrowRightIcon size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Interested in Our Portfolio?</h2>
            <p>Learn more about investment opportunities and partnerships with Fuwali.</p>
            <Link to="/contact" className="btn btn-secondary">
              Contact Us <ArrowRightIcon size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Portfolio;
